/**
 * Typed wrappers around the lexruntime activity gateway methods.
 *
 * Backend handlers live in
 * [extensions/lexiun/src/activity/handlers.ts](../../../lexruntime/openclaw/extensions/lexiun/src/activity/handlers.ts)
 * (`createActivityGatewayMethods`). The wire shape is loose
 * (Record<string, unknown>) because the runtime parses params
 * permissively — these wrappers narrow the surface for callers
 * inside lexportal.
 *
 * All requests are scoped to a single per-user lexruntime container
 * (the connected gateway). Cross-user views require switching the
 * runtime context (see `useRuntimeContext`).
 */

import type { ActivityEvent } from "@lexiun/lx-obs";

export interface ActivityListParams {
  /** Lower bound `ts` (ms epoch). Inclusive. */
  since?: number;
  /** Upper bound `ts` (ms epoch). Exclusive. */
  until?: number;
  user_id?: string;
  session_id?: string;
  case_id?: string;
  source?: ActivityEvent["source"];
  subsystem?: string;
  category?: ActivityEvent["category"];
  type?: string;
  agent_id?: string;
  task_id?: string;
  /** Opaque cursor returned by the previous page. */
  cursor?: string;
  /** Page size (server caps at 200). */
  limit?: number;
}

export interface ActivityListResult {
  events: ActivityEvent[];
  next_cursor?: string;
}

export interface ActivityGetResult {
  event: ActivityEvent | null;
  /** Up to N events from the same session within ±60 s. */
  context: ActivityEvent[];
}

export interface ActivitySummaryParams {
  since: number;
  until: number;
  bucket?: "hour" | "day";
}

export interface ActivitySummaryTotals {
  total: number;
  by_source: Record<string, number>;
  by_category: Record<string, number>;
  by_type: Record<string, number>;
  sessions: number;
  errors: number;
  harness_runs: number;
}

export interface ActivitySummarySeriesPoint {
  ts: number;
  count: number;
  errors: number;
}

export interface ActivitySummaryResult {
  totals: ActivitySummaryTotals;
  series: ActivitySummarySeriesPoint[];
}

type RpcFn = <T = unknown>(method: string, params?: unknown) => Promise<T>;

/** Thin client over `useGatewayRpc()`'s `rpc` callable. */
export class ActivityRpcClient {
  constructor(private readonly rpc: RpcFn) {}

  list(params: ActivityListParams = {}): Promise<ActivityListResult> {
    return this.rpc<ActivityListResult>("activity.list", stripUndefined(params));
  }

  get(id: string): Promise<ActivityGetResult> {
    return this.rpc<ActivityGetResult>("activity.get", { id });
  }

  summary(params: ActivitySummaryParams): Promise<ActivitySummaryResult> {
    return this.rpc<ActivitySummaryResult>(
      "activity.summary",
      stripUndefined(params),
    );
  }
}

/** Drop undefined values so the wire payload doesn't include them. */
export function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== "") out[k] = v;
  }
  return out as Partial<T>;
}
